import { Fragment, useState, useMemo, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationTriangleIcon, CalendarIcon } from "@heroicons/react/24/outline";
import { ClipLoader } from "react-spinners";

// Rental-related event types that must be sequenced together per reservation
const RENTAL_RELATED_TYPES = new Set(["Rental Days", "Extension", "Reduction"]);

/**
 * SequenceConfirmationPopup Component
 * Shows a confirmation dialog listing billing events about to be sequenced
 * Also supports bulk company billing mode (showing company names instead of events)
 * 
 * @param {Array} events - Array of billing event objects to be sequenced (for normal mode)
 * @param {Array} companies - Array of company objects { id, name } for bulk company mode
 * @param {function} onConfirm - Callback when user confirms sequencing (receives businessDateTime, selectedBranchCodes, companies, excludedEventIds)
 * @param {function} onCancel - Callback when user cancels
 * @param {boolean} sequencing - Whether sequencing is in progress
 * @param {boolean} isCompanyBilling - Whether this is company-level billing (shows branch selection)
 * @param {boolean} isBulkCompanyBilling - Whether this is bulk company billing (shows company list, no branches)
 */
const SequenceConfirmationPopup = ({ 
  events = [], 
  companies = [], 
  onConfirm, 
  onCancel, 
  sequencing, 
  isCompanyBilling = false,
  isBulkCompanyBilling = false,
  submitError = null
}) => {
  // Build branch summary with counts and timezone
  const branchSummary = useMemo(() => {
    const summary = {};
    events.forEach(ev => {
      const branchCode = ev.branch?.code || ev.branch?.Name || "Unknown";
      const branchName = ev.branch?.name || ev.branch?.Name || ev.branch?.code || "Unknown Branch";
      const branchTimezone = ev.branch?.timezone ?? 0;
      if (!summary[branchCode]) {
        summary[branchCode] = { code: branchCode, name: branchName, count: 0, timezone: branchTimezone };
      }
      summary[branchCode].count += 1;
      // Update timezone if this event has one and current is 0
      if (branchTimezone !== 0 && summary[branchCode].timezone === 0) {
        summary[branchCode].timezone = branchTimezone;
      }
    });
    return Object.values(summary);
  }, [events]);

  // Initialize selected branches - all branches selected by default
  const [selectedBranches, setSelectedBranches] = useState(() => {
    return branchSummary.map(b => b.code);
  });

  // ── Selective billing: track which event IDs are selected (all by default) ──
  const [selectedEventIds, setSelectedEventIds] = useState(() => {
    return new Set(events.map(ev => ev.id).filter(Boolean));
  });

  // Default to current device time in datetime-local format
  const getDefaultDateTime = () => {
    const now = new Date();
    // Format as YYYY-MM-DDTHH:mm for datetime-local input (uses device local time)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [businessDateTime, setBusinessDateTime] = useState(getDefaultDateTime());

  // Toggle branch selection
  const toggleBranch = (branchCode) => {
    setSelectedBranches(prev => {
      if (prev.includes(branchCode)) {
        return prev.filter(c => c !== branchCode);
      } else {
        return [...prev, branchCode];
      }
    });
  };

  // Filter events based on selected branches
  const filteredEvents = useMemo(() => {
    return events.filter(ev => {
      const branchCode = ev.branch?.code || ev.branch?.Name || "Unknown";
      return selectedBranches.includes(branchCode);
    });
  }, [events, selectedBranches]);

  // The visible event list (branch-filtered for company billing, all events otherwise)
  const visibleEvents = isCompanyBilling ? filteredEvents : events;

  // ── Toggle a single event's selection ──
  const toggleEvent = useCallback((eventId) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  }, []);

  // ── Select All / Deselect All toggle ──
  const allVisibleSelected = visibleEvents.length > 0 && visibleEvents.every(ev => selectedEventIds.has(ev.id));
  const someVisibleSelected = visibleEvents.some(ev => selectedEventIds.has(ev.id));

  const toggleAllVisible = useCallback(() => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        // Deselect all visible
        visibleEvents.forEach(ev => { if (ev.id) next.delete(ev.id); });
      } else {
        // Select all visible
        visibleEvents.forEach(ev => { if (ev.id) next.add(ev.id); });
      }
      return next;
    });
  }, [allVisibleSelected, visibleEvents]);

  // ── Rental-group validation ──
  // For each reservation, if ANY rental-related event is selected, ALL must be.
  const rentalValidation = useMemo(() => {
    // Group rental-related events by reservation ID
    const rentalByReservation = visibleEvents.reduce((acc, ev) => {
      if (!RENTAL_RELATED_TYPES.has(ev.type)) return acc;
      const resId = typeof ev.reservation === "object" ? ev.reservation?.id : ev.reservation;
      if (!resId) return acc;
      if (!acc[resId]) acc[resId] = [];
      acc[resId].push(ev);
      return acc;
    }, {});

    // Check each reservation group
    const errors = {}; // eventId -> error message
    let hasError = false;
    for (const [resId, rentalEvents] of Object.entries(rentalByReservation)) {
      if (rentalEvents.length <= 1) continue; // Single rental event — no constraint
      const selectedCount = rentalEvents.filter(ev => selectedEventIds.has(ev.id)).length;
      const isPartial = selectedCount > 0 && selectedCount < rentalEvents.length;
      if (isPartial) {
        hasError = true;
        for (const ev of rentalEvents) {
          errors[ev.id] = "Rental-related items (Rental Days, Extensions, Reductions) must be billed together for the same reservation. Select all or none.";
        }
      }
    }
    return { errors, hasError };
  }, [visibleEvents, selectedEventIds]);

  // Count of selected visible events
  const selectedVisibleCount = visibleEvents.filter(ev => selectedEventIds.has(ev.id)).length;

  // Check if any branch is selected
  const hasSelectedBranches = selectedBranches.length > 0;

  // Can we submit?
  const canSubmit = !sequencing
    && !rentalValidation.hasError
    && (isBulkCompanyBilling || selectedVisibleCount > 0)
    && (!isCompanyBilling || hasSelectedBranches);

  const formatAmount = (amount) => {
    if (!amount) return "0.00";
    return Number(amount).toFixed(2);
  };

  const getEventTypeLabel = (event) => {
    let label = event.type;
    if (event.is_credit_note) {
      label = `${label} (Credit)`;
    }
    
    // Add plate number if available to help differentiate items
    const plate = event.reservation?.vehicle?.plate;
    if (plate) {
      label = `${label} [${plate}]`;
    }
    
    return label;
  };

  // Compute excluded IDs (all event IDs that exist but are NOT selected)
  const getExcludedEventIds = () => {
    return events
      .map(ev => ev.id)
      .filter(id => id && !selectedEventIds.has(id));
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !sequencing && onCancel()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon
                      className="h-6 w-6 text-yellow-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      {isBulkCompanyBilling ? "Bulk Company Billing" : "Confirm Billing Events Sequencing"}
                    </Dialog.Title>
                    <div className="mt-4">
                      {/* Bulk Company Mode */}
                      {isBulkCompanyBilling ? (
                        <>
                          <p className="text-sm text-gray-500 mb-4">
                            You are about to sequence billing events for{" "}
                            <strong>{companies.length} compan{companies.length === 1 ? "y" : "ies"}</strong>.
                            All branches will be included automatically.
                          </p>

                          {/* Companies List */}
                          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md mb-4">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Company Name
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {companies.map((company, idx) => (
                                  <tr key={company.id || idx} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-sm text-gray-900">
                                      {company.name || company.Name || `Company #${company.id}`}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Business Date & Time Input */}
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                            <label htmlFor="business-datetime-bulk" className="block text-sm font-medium text-gray-700 mb-2">
                              <CalendarIcon className="h-4 w-4 inline-block mr-1" />
                              Business Date & Time
                            </label>
                            <input
                              type="datetime-local"
                              id="business-datetime-bulk"
                              name="business-datetime-bulk"
                              value={businessDateTime}
                              onChange={(e) => setBusinessDateTime(e.target.value)}
                              disabled={sequencing}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              This date will be used for all selected companies.
                            </p>
                          </div>

                          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <p className="text-xs text-blue-700">
                              <strong>Note:</strong> Companies will be processed one by one. 
                              If an error occurs, the process will stop and you will be notified.
                            </p>
                          </div>
                        </>
                      ) : (
                        /* Normal Events Mode */
                        <>
                      <p className="text-sm text-gray-500 mb-4">
                        {selectedVisibleCount === visibleEvents.length
                          ? `The following ${visibleEvents.length} billing event${visibleEvents.length !== 1 ? 's' : ''} will be locked and sequenced.`
                          : `${selectedVisibleCount} of ${visibleEvents.length} billing event${visibleEvents.length !== 1 ? 's' : ''} selected for sequencing.`
                        }
                        {" "}Once sequenced, the items and the sequence cannot be edited or deleted.
                        {selectedVisibleCount < visibleEvents.length && (
                          <span className="block mt-1 text-xs text-amber-600">
                            Unselected items will remain pending and appear on the next bill.
                          </span>
                        )}
                      </p>

                      {/* Branch Selection - Only for company billing */}
                      {isCompanyBilling && branchSummary.length > 0 && (
                          <div className="mb-4 text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-200">
                            <strong className="block mb-2">Select Branches to Sequence:</strong>
                            <div className="space-y-2">
                              {branchSummary.map((branch) => (
                                <label 
                                  key={branch.code} 
                                  className={`flex items-center gap-2 cursor-pointer p-2 rounded transition-colors ${
                                    selectedBranches.includes(branch.code) 
                                      ? 'bg-indigo-50 border border-indigo-200' 
                                      : 'bg-white border border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedBranches.includes(branch.code)}
                                    onChange={() => toggleBranch(branch.code)}
                                    disabled={sequencing}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                                  />
                                  <span className="flex-1">
                                    {branch.name} ({branch.code})
                                  </span>
                                  <span className="text-gray-500 text-xs">
                                    {branch.count} event{branch.count !== 1 ? 's' : ''}
                                  </span>
                                </label>
                              ))}
                            </div>
                            {!hasSelectedBranches && (
                              <p className="mt-2 text-xs text-red-600">
                                Please select at least one branch to sequence.
                              </p>
                            )}
                          </div>
                        )}

                      {/* Branch Summary (read-only) - Only for non-company billing when branch data exists */}
                      {!isCompanyBilling && events.some(e => e.branch) && branchSummary.length > 0 && (
                          <div className="mb-4 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                            <strong>Summary by Branch:</strong>
                            <ul className="list-disc pl-5 mt-1">
                              {branchSummary.map((branch) => (
                                <li key={branch.code}>{branch.name} ({branch.code}): {branch.count} event{branch.count !== 1 ? 's' : ''}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                      {/* Rental-group validation error banner */}
                      {rentalValidation.hasError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-xs text-red-700">
                            <strong>⚠ Cannot sequence:</strong> Rental-related items (Rental Days, Extensions, Reductions) for the same reservation must be billed together. Select all or none of them.
                          </p>
                        </div>
                      )}

                      {/* Backend submission error banner */}
                      {submitError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-sm text-red-700">
                            <strong>⚠ Sequencing Error:</strong> {submitError}
                          </p>
                        </div>
                      )}

                      {/* Events List with Checkboxes */}
                      <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              {/* Select All checkbox */}
                              <th className="px-3 py-2 text-left">
                                <input
                                  type="checkbox"
                                  checked={allVisibleSelected}
                                  ref={el => {
                                    if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                                  }}
                                  onChange={toggleAllVisible}
                                  disabled={sequencing}
                                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                                />
                              </th>
                              {visibleEvents.some(e => e.branch) && (
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Branch
                                </th>
                              )}
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {visibleEvents.map((event, idx) => {
                              const isSelected = selectedEventIds.has(event.id);
                              const hasRentalError = !!rentalValidation.errors[event.id];
                              return (
                                <tr
                                  key={event.id || idx}
                                  className={`transition-colors cursor-pointer ${
                                    hasRentalError
                                      ? "bg-red-50 hover:bg-red-100"
                                      : isSelected
                                        ? "hover:bg-gray-50"
                                        : "bg-gray-100 opacity-60 hover:opacity-80"
                                  }`}
                                  onClick={() => !sequencing && event.id && toggleEvent(event.id)}
                                >
                                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => event.id && toggleEvent(event.id)}
                                      disabled={sequencing}
                                      className={`h-4 w-4 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50 ${
                                        hasRentalError ? "text-red-600 border-red-400" : "text-indigo-600"
                                      }`}
                                    />
                                  </td>
                                  {visibleEvents.some(e => e.branch) && (
                                    <td className="px-3 py-2 text-sm text-gray-500">
                                      {event.branch?.code || "-"}
                                    </td>
                                  )}
                                  <td className="px-3 py-2 text-sm text-gray-900">
                                    {getEventTypeLabel(event)}
                                    {hasRentalError && (
                                      <span className="block text-xs text-red-600 mt-0.5">
                                        Must bill with other rental items
                                      </span>
                                    )}
                                  </td>
                                  <td className={`px-3 py-2 text-sm text-right ${event.is_credit_note ? "text-green-600" : "text-gray-900"
                                    }`}>
                                    {event.is_credit_note ? "-" : ""}{formatAmount(event.amount)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Business Date & Time Input */}
                      <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                        <label htmlFor="business-datetime" className="block text-sm font-medium text-gray-700 mb-2">
                          <CalendarIcon className="h-4 w-4 inline-block mr-1" />
                          Business Date & Time
                        </label>
                        <input
                          type="datetime-local"
                          id="business-datetime"
                          name="business-datetime"
                          value={businessDateTime}
                          onChange={(e) => setBusinessDateTime(e.target.value)}
                          disabled={sequencing}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          This date and time will be converted to UTC based on the branch timezone.
                        </p>
                      </div>

                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-xs text-blue-700">
                          <strong>Note:</strong> Events will be grouped and sequenced according to business rules.
                        </p>
                      </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={!canSubmit}
                    className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${
                      !canSubmit
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary hover:bg-blue-600"
                      }`}
                    onClick={() => {
                      // Compute excluded IDs from the full events list
                      const excludedIds = getExcludedEventIds();
                      // Send the datetime-local value as-is (YYYY-MM-DDTHH:mm format)
                      // Backend will handle timezone conversion using branch.timezone from DB
                      if (isBulkCompanyBilling) {
                        onConfirm(businessDateTime, null, companies, excludedIds);
                      } else {
                        onConfirm(businessDateTime, isCompanyBilling ? selectedBranches : null, null, excludedIds);
                      }
                    }}
                  >
                    {sequencing ? (
                      <>
                        <ClipLoader color="#ffffff" size={16} className="mr-2" />
                        Sequencing...
                      </>
                    ) : isBulkCompanyBilling ? (
                      `Bill ${companies.length} Compan${companies.length === 1 ? "y" : "ies"}`
                    ) : (
                      `Finalize Bill (${selectedVisibleCount} event${selectedVisibleCount !== 1 ? 's' : ''})`
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={sequencing}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={onCancel}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SequenceConfirmationPopup;
